/**
 * M.E.A. (Marta Email Assistant) Configuration
 *
 * Agent configuration and metadata
 */

import type { AIAgent } from '../types.js';

// =============================================================================
// AGENT CONFIGURATION
// =============================================================================

export const MEA_AGENT_ID = 'mea';
export const MEA_AGENT_VERSION = '1.0.0';

export const MEA_AGENT: AIAgent = {
  id: MEA_AGENT_ID,
  name: 'Marta Email Assistant',
  description: 'Classifica le email automaticamente e impara dalle correzioni utente',
  version: MEA_AGENT_VERSION,
  enabled: true,

  capabilities: [
    {
      id: 'classify-email',
      name: 'Classify Email',
      description: 'Analyzes subject, sender and body to classify emails into: work_important, work_routine, advertisement, spam',
      inputSchema: {
        type: 'object',
        properties: {
          subject: { type: 'string', description: 'Email subject' },
          from: { type: 'string', description: 'Sender email address' },
          body: { type: 'string', description: 'Email body text' },
          fromName: { type: 'string', description: 'Sender display name' },
        },
        required: ['subject', 'from', 'body'],
      },
      outputSchema: {
        type: 'object',
        properties: {
          classification: {
            type: 'string',
            enum: ['work_important', 'work_routine', 'advertisement', 'spam'],
          },
          confidence: { type: 'number', minimum: 0, maximum: 1 },
          reason: { type: 'string', maxLength: 100 },
          suggestedCompanyName: { type: 'string' },
        },
        required: ['classification', 'confidence', 'reason'],
      },
    },
  ],

  workflows: ['email-classification', 'inbox-triage'],
  triggers: ['email.received', 'email.manual-classify'],

  metrics: {
    totalCalls: 0,
    successRate: 0,
    avgLatencyMs: 0,
    correctionRate: 0,
  },

  trainingDataCount: 0,
  lastTrainedAt: undefined,
};

// =============================================================================
// CLASSIFICATION PROMPT
// =============================================================================

export const CLASSIFICATION_PROMPT = `You are an email classification assistant. Analyze the email and return a JSON with:
- classification: one of the following categories:
  - "work_important": work emails requiring attention/action
  - "work_routine": routine work emails (notifications, confirmations, low priority)
  - "advertisement": marketing, newsletters, promotions
  - "spam": spam, scams, phishing
- confidence: number from 0.0 to 1.0 indicating certainty
- reason: brief explanation (max 100 characters)
- suggestedCompanyName: if identifiable, the sender company name (or null)

Classification criteria (in priority order):

1. SPAM: scams, phishing, suspicious links, adult content, lottery wins, unknown inheritances, requests for personal data, inconsistent sender identity
   → Always classify as "spam" if red flags present

2. WORK_IMPORTANT: emails requiring action or response
   - Direct requests from clients, colleagues, suppliers
   - Invoices, orders, quotes to process
   - Meeting requests, deadline reminders
   - Problems or issues to resolve
   - New business opportunities

3. WORK_ROUTINE: work-related but low priority, no action required
   - Automatic notifications (system alerts, backups, logs)
   - Read receipts, delivery confirmations
   - Newsletters from business tools/services
   - Automatic reports
   - CC emails where you're not the main recipient
   - Calendar updates, recurring meeting reminders

4. ADVERTISEMENT: commercial communications
   - Marketing emails, promotions, discounts
   - Product announcements
   - Event invitations (commercial)
   - Newsletters (not business tools)

Respond ONLY with a valid JSON object, no other text.`;

// =============================================================================
// DEFAULT SETTINGS
// =============================================================================

export const MEA_SETTINGS = {
  /** Model to use for classification */
  model: 'claude-3-haiku-20240307',
  /** Maximum tokens for response */
  maxTokens: 200,
  /** Maximum body length to send */
  maxBodyLength: 2000,
  /** Auto-approve corrections with this confidence threshold */
  autoApproveThreshold: 0.95,
  /** Minimum occurrences before auto-approve */
  minOccurrencesForAutoApprove: 3,
};
